import type { TGetAccessToken } from '../types';

import { captureException, addBreadcrumb } from '@/modules/sentry';
import {
  EAuthErrorCode,
  isNonRecoverableAuthErrorCode,
  isSilentAuthErrorCode,
} from './auth-error-codes';
import { mapAuth0SdkErrorToCode } from './auth0-error-map';

/**
 * Default configuration for the token manager.
 */
const DEFAULT_CONFIG = {
  /** Maximum number of retry attempts for transient failures */
  maxRetries: 2,
  /** Base delay in milliseconds for exponential backoff */
  baseDelayMs: 500,
  /** Maximum delay in milliseconds between retries */
  maxDelayMs: 3000,
  /** Time-to-live for cached token promises in milliseconds.
   * This deduplicates concurrent requests that arrive within this window. */
  deduplicationWindowMs: 5000,
} as const;

/**
 * Token fetch result indicating whether the fetch was successful
 * and providing the token or error details.
 */
export type TTokenFetchResult =
  | { success: true; token: string }
  | { success: false; error: Error; requiresLogout: boolean };

/**
 * Configuration options for the token manager.
 */
export interface ITokenManagerConfig {
  /** Maximum number of retry attempts for transient failures */
  maxRetries?: number;
  /** Base delay in milliseconds for exponential backoff */
  baseDelayMs?: number;
  /** Maximum delay in milliseconds between retries */
  maxDelayMs?: number;
  /** Time-to-live for cached token promises in milliseconds */
  deduplicationWindowMs?: number;
}

/**
 * Determines if an auth error is non-recoverable (requires logout/re-auth).
 *
 * @param error - The error thrown by getAccessTokenSilently
 * @returns true if the error indicates the session is broken and requires re-authentication
 */
export function isNonRecoverableAuthError(error: unknown): boolean {
  return isNonRecoverableAuthErrorCode(mapAuth0SdkErrorToCode(error));
}

/**
 * Calculates the delay for exponential backoff with jitter.
 *
 * @param attempt - The current retry attempt (0-indexed)
 * @param baseDelayMs - Base delay in milliseconds
 * @param maxDelayMs - Maximum delay cap in milliseconds
 * @returns Delay in milliseconds with added jitter
 */
export function calculateBackoffDelay(
  attempt: number,
  baseDelayMs: number,
  maxDelayMs: number,
): number {
  const exponentialDelay = baseDelayMs * Math.pow(2, attempt);
  const cappedDelay = Math.min(exponentialDelay, maxDelayMs);
  // Add jitter (0-50% of the delay) to prevent thundering herd
  const jitter = cappedDelay * Math.random() * 0.5;
  return cappedDelay + jitter;
}

/**
 * Creates a promise that resolves after a specified delay.
 *
 * @param ms - Delay in milliseconds
 * @returns Promise that resolves after the delay
 */
function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Manages Auth0 token fetching with retry logic, request deduplication,
 * and proper error categorization.
 *
 * Key features:
 * - **Retry with exponential backoff**: Transient failures (network errors, timeouts)
 *   are retried up to `maxRetries` times with increasing delays.
 * - **Request deduplication**: Concurrent API requests share a single in-flight
 *   token fetch, preventing Auth0 rate limiting and race conditions.
 * - **Error categorization**: Distinguishes between recoverable errors (retry) and
 *   non-recoverable errors (logout), with proper Sentry reporting.
 *
 * @example
 * ```typescript
 * const tokenManager = createTokenManager();
 *
 * // In axios interceptor:
 * const result = await tokenManager.getToken(getAccessTokenSilently);
 * if (result.success) {
 *   config.headers.Authorization = `Bearer ${result.token}`;
 * } else if (result.requiresLogout) {
 *   logout();
 * }
 * ```
 */
export function createTokenManager(config?: ITokenManagerConfig) {
  const { maxRetries, baseDelayMs, maxDelayMs, deduplicationWindowMs } = {
    ...DEFAULT_CONFIG,
    ...config,
  };

  /** In-flight token promise for deduplication */
  let pendingTokenPromise: Promise<TTokenFetchResult> | null = null;

  /** Timestamp of when the pending promise was created */
  let pendingTokenTimestamp = 0;

  /**
   * Fetches a token with retry logic for transient failures.
   *
   * @param getAccessTokenSilently - Token getter from `useAuth()`
   * @returns Token fetch result with success/failure details
   */
  async function fetchTokenWithRetry(
    getAccessTokenSilently: TGetAccessToken,
  ): Promise<TTokenFetchResult> {
    let lastError: unknown;

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        const token = await getAccessTokenSilently();
        return { success: true, token };
      } catch (error) {
        lastError = error;
        const errorCode = mapAuth0SdkErrorToCode(error);

        // Non-recoverable errors should not be retried
        if (isNonRecoverableAuthErrorCode(errorCode)) {
          const requiresLogout =
            errorCode === EAuthErrorCode.MISSING_REFRESH_TOKEN;

          addBreadcrumb({
            category: 'auth',
            message: `Non-recoverable auth error: ${error instanceof Error ? error.message : 'Unknown'}`,
            level: 'error',
          });

          // A missing refresh token is a session-lifecycle condition today, but
          // a spike could indicate a provider scope regression. Rate-track at
          // warning level with a fixed fingerprint so all occurrences collapse
          // into one Sentry issue — monitor the rate, not individual events.
          if (requiresLogout) {
            captureException(error, {
              level: 'warning',
              fingerprint: ['auth', 'missing-refresh-token'],
              tags: {
                module: 'auth',
                action: 'getAccessTokenSilently',
                errorType: 'missing-refresh-token',
              },
            });
          } else if (!isSilentAuthErrorCode(errorCode)) {
            captureException(error, {
              tags: {
                module: 'auth',
                action: 'getAccessTokenSilently',
                errorType: 'non-recoverable',
              },
              extra: {
                attempt,
                requiresLogout,
              },
            });
          }

          return {
            success: false,
            error:
              error instanceof Error
                ? error
                : new Error('Non-recoverable auth error'),
            requiresLogout,
          };
        }

        // Log retry attempt
        addBreadcrumb({
          category: 'auth',
          message: `Token fetch attempt ${attempt + 1}/${maxRetries + 1} failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
          level: 'warning',
        });

        // Retry with backoff if we have attempts remaining
        if (attempt < maxRetries) {
          const backoffMs = calculateBackoffDelay(
            attempt,
            baseDelayMs,
            maxDelayMs,
          );
          await delay(backoffMs);
        }
      }
    }

    // All retries exhausted - report to Sentry
    captureException(lastError, {
      tags: {
        module: 'auth',
        action: 'getAccessTokenSilently',
        errorType: 'retries-exhausted',
      },
      extra: {
        maxRetries,
        originalError:
          lastError instanceof Error
            ? { message: lastError.message, name: lastError.name }
            : String(lastError),
      },
    });

    return {
      success: false,
      error:
        lastError instanceof Error
          ? lastError
          : new Error('Failed to get access token after retries'),
      requiresLogout: false,
    };
  }

  /**
   * Gets an access token, deduplicating concurrent requests.
   *
   * If a token fetch is already in-flight within the deduplication window,
   * the same promise is returned to all concurrent callers. This prevents
   * multiple simultaneous calls to Auth0's token endpoint.
   *
   * @param getAccessTokenSilently - Token getter from `useAuth()`
   * @returns Token fetch result with success/failure details
   */
  async function getToken(
    getAccessTokenSilently: TGetAccessToken,
  ): Promise<TTokenFetchResult> {
    const now = Date.now();

    // Reuse in-flight promise if within the deduplication window
    if (
      pendingTokenPromise &&
      now - pendingTokenTimestamp < deduplicationWindowMs
    ) {
      return pendingTokenPromise;
    }

    // Create new token fetch promise
    pendingTokenTimestamp = now;
    pendingTokenPromise = fetchTokenWithRetry(getAccessTokenSilently).finally(
      () => {
        // Clear the pending promise after it settles so future requests
        // trigger a fresh fetch (unless another request already replaced it)
        if (pendingTokenTimestamp === now) {
          pendingTokenPromise = null;
        }
      },
    );

    return pendingTokenPromise;
  }

  /**
   * Resets the token manager state. Useful for testing or after logout.
   */
  function reset(): void {
    pendingTokenPromise = null;
    pendingTokenTimestamp = 0;
  }

  return {
    getToken,
    reset,
  };
}
